import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { AbstractDiffusionSessionService } from '@zero/shared/util-abstract-services';
import { AuthStateFacade } from '@zero/shared/util-global-state';
import { DividendYield, DividendYieldTotal, NewsItem } from '@zero/shared/util-models';
import { datatypes, JSON, Session } from 'diffusion';
import { environment } from 'environments/environment';
import { filter, map, Observable, switchMap, tap } from 'rxjs';
import { ChartAndStats } from '../entities/chart-and-stats.entity';
import { Dividend } from '../entities/dividends.entity';
import { MyTrades } from '../entities/my-trades.entity';
import { Trade } from '../entities/trade.entity';
import { Weighting } from '../entities/weighting.entity';

@Injectable({
  providedIn: 'root',
})
export class StatisticsInfrastructure {
  private readonly _dataType = datatypes.json();
  private readonly _diffusion = inject(AbstractDiffusionSessionService);
  private readonly _http = inject(HttpClient);
  private readonly _authStateFacade = inject(AuthStateFacade);
  private readonly _statsUri = environment.apihost + environment.stats;
  private readonly _lastTradesTopic = '/tradeticker/lastx';
  private readonly _tradeTicksTopic = '/tradeticker/last';

  public getTradeTicker(): Observable<Trade[]> {
    return this._diffusion.diffusionSession$.pipe(
      filter((session: Session) => !!session),
      map((session) => session.fetchRequest()),
      map((request) => request.withValues(this._dataType)),
      switchMap((items) => items.fetch(this._lastTradesTopic)),
      map((result) => result.results()),
      filter((trades) => Array.isArray(trades) && trades?.length > 0),
      map((trades) => trades[0].value().get() as Trade[]),
      filter((trades) => Array.isArray(trades)),
      map((trades) =>
        [...trades].sort(({ execDateTime: a }: Trade, { execDateTime: b }: Trade): number => {
          if (a > b) {
            return -1;
          }
          return a < b ? 1 : 0;
        }),
      ),
    );
  }

  public listenToTradeTicks(): Observable<Trade> {
    return this._diffusion.diffusionSession$.pipe(
      filter((session: Session) => !!session),
      tap((session) => session.select(this._tradeTicksTopic)),
      switchMap(
        (session) =>
          new Observable<Trade>((subscriber) => {
            session
              .addStream(this._tradeTicksTopic, this._dataType)
              .on('value', (_, __, value: JSON) => subscriber.next(value.get() as Trade));
          }),
      ),
    );
  }

  public unsubscribeFromTradeTicks(): Observable<void> {
    return this._diffusion.diffusionSession$.pipe(
      filter((session: Session) => !!session),
      switchMap((session) => session.unsubscribe(this._tradeTicksTopic)),
    );
  }

  public loadMyTrades(): Observable<MyTrades> {
    return this._http.get<MyTrades>(`${this._statsUri}/customer/${this._authStateFacade.customerId}/orders`);
  }

  public loadWeightingByAssetType(): Observable<Weighting> {
    return this._http.get<Weighting>(`${this._statsUri}/customer/${this._authStateFacade.customerId}/positions/volumesbyassettype`);
  }

  public loadWeightingByTopPositions(): Observable<Weighting> {
    return this._http.get<Weighting>(`${this._statsUri}/customer/${this._authStateFacade.customerId}/positions/top`);
  }

  public loadWeightingByPositions(): Observable<Weighting> {
    return this._http.get<Weighting>(`${this._statsUri}/customer/${this._authStateFacade.customerId}/positions`);
  }

  public loadMyNews(isins: string[]): Observable<NewsItem[]> {
    const maxNews = 5;
    const url = `${environment.apihost + environment.instrumentNews}/isins?maxNews=${maxNews}`;
    return this._http.post<NewsItem[]>(url, isins);
  }

  public getChartAndStats(): Observable<ChartAndStats> {
    return this._http.get<ChartAndStats>(`${this._statsUri}/customer/${this._authStateFacade.customerId}/portfolio`);
  }

  public loadDividends(): Observable<Dividend[]> {
    return this._http.get<Dividend[]>(`${this._statsUri}/customer/${this._authStateFacade.customerId}/dividends`);
  }

  public loadDividendYields(): Observable<DividendYield[]> {
    return this._http.get<DividendYield[]>(`${this._statsUri}/customer/${this._authStateFacade.customerId}/dividendyields`);
  }

  public loadDividendYieldTotalByPosition(isin: string): Observable<DividendYieldTotal> {
    return this._http.get<DividendYieldTotal>(`${this._statsUri}/customer/${this._authStateFacade.customerId}/dividendyieldtotal/${isin}`);
  }
}
